import styled from '@emotion/styled';
import { list, lpPageColors, mainColors } from 'src/assets/colors';
import { fontSize, fontWeight } from 'src/assets/fonts-config';
import { ArrowIcon } from 'src/assets/icons/ArrowIcon';
import { DragIcon } from 'src/assets/new-icon/DragIcon';
import { ChevronIcon } from 'src/assets/new-icon/ChevronIcon';
import { RemoveTrashIcon } from 'src/assets/icons/RemoveTrashIcon';
import { SerializedStyles, css } from '@emotion/react';
import { ImagePlaceholderIcon } from 'src/assets/new-icon/ImagePlaceholderIcon';
import { Toggle } from 'src/domains/layouts/shared/toggle/Toggle';
import { ScrollBarVertical } from 'src/domains/layouts/shared/scrollbar/Scrollbar.style';

export const HeaderWrapper = styled('header', { label: 'HeaderWrapper' })`
    display: flex;
    flex-flow: column nowrap;
    gap: 16px;
    margin-bottom: 20px;
    padding: 0 8px;
`;

interface ListContainerType {
    areLoading: boolean;
}

const cmsListContainerHeight = 'calc(100vh - 104px)';

export const ListContainer = styled(ScrollBarVertical, { label: 'ListContainer' })<ListContainerType>`
    min-height: 50px;
    max-height: ${cmsListContainerHeight};
    opacity: ${({ areLoading }): string => areLoading === true ? '0.3' : '1' };
    pointer-events: ${({ areLoading }): string => areLoading === true ? 'none' : 'auto' };
    position: relative;
    &:empty {
        display: contents;
    }
`;

interface ListItemType {
    /** min-height */
    boxHeight?: string;
    homepage?: boolean;
    isEdited?: boolean;
    isDragged?: boolean;
    isEmpty?: boolean;
    subsection?: 'one' | 'two';
    /** flex direction - remove props after headline and casino banners refactor */
    direction: 'row' | 'column';
}

const setBorder = (isDragged: boolean | undefined, isEmpty: boolean | undefined, subsection: 'one' | 'two' | undefined): SerializedStyles => {
    const borderColor = subsection === 'one' ? list.listItem.subsectionOne.borderColor : subsection === 'two' ? list.listItem.subsectionTwo.borderColor : list.listItem.borderColor;

    if (isEmpty === true) {
        return css`
            border: none;
        `;
    }

    if (isDragged === true) {
        return css`
            border: 1px solid ${borderColor};;
        `;
    }

    return css`
        border-bottom: 1px solid ${borderColor};
    `;
};

export const ListWrapper = styled('div', { label: 'ListWrapper' })`
    padding: 16px 16px 24px 16px;
    background-color: ${list.bgColor};
`;

export const ListContentWrapper = styled('div', { label: 'ListContentWrapper' })<{isDragged?: boolean}>`
    opacity: ${({ isDragged }): number => isDragged === true ? 0.5 : 1};
`;

export const ListItem = styled('div', { label: 'ListItem' })<ListItemType>`
    background: ${({ subsection, isEdited }): string => subsection === 'one' ? list.listItem.subsectionOne.bgColor : subsection === 'two' ? list.listItem.subsectionTwo.bgColor : isEdited === true ? list.listItem.bgColorSecondary : list.listItem.bgColor};
    ${({ isDragged, isEmpty, subsection }): SerializedStyles => setBorder(isDragged, isEmpty, subsection)};
    color: ${list.listItem.txtColor};
    display: flex;
    flex-flow: ${({ direction }): string => direction === 'row' ? 'row nowrap' : 'column nowrap'};
    font-family: var(--fontFamilyPrimary);
    justify-content: space-between;
    ${({ direction }): string => direction === 'row' ? 'align-items: center' : ''};
    min-height: ${({ boxHeight }): string => boxHeight === undefined ? '48px' : boxHeight};
    padding: 12px 8px;
    position: relative;
`;

type LabelPropsType = {
    subsection?: 'one' | 'two';
    hasSubsection?: boolean;
}

export const LabelWrapper = styled('div', { label: 'LabelWrapper' })<LabelPropsType>`
    display: flex;
    align-items: center;
    ${({ hasSubsection }): string => hasSubsection === true ? 'cursor: pointer' : ''};
    font-weight: ${({ subsection, hasSubsection }): string => hasSubsection === true || subsection === 'one' ? fontWeight.bold : fontWeight.medium};
    font-size: ${fontSize.small};
    line-height: 1.3334;
`;

export const ColumnWrapper = styled('div', { label: 'ColumnWrapper' })`
    display: flex;
    flex-flow: column nowrap;
`;

export const ItemDetails = styled('div', { label: 'ItemDetails' })`
    width: 200px;
`;

export const ItemDescription = styled('div', { label: 'ItemDescription' })`
    font-size: ${fontSize.xSmall};
    margin-top: 8px;
    font-weight: ${fontWeight.regular};
`;

interface ImageContainerPropsType {
    imgSrc: string;
}

export const ImageContainer = styled('div', { label: 'ImageContainer' })<ImageContainerPropsType>`
    ${({ imgSrc }): string => imgSrc === '' ? `background-color: ${list.listItem.image.bgColor}` : `background-image: url(${imgSrc})`};
    background-size: cover;
    height: 56px;
    width: 56px;
    position: relative;
    margin: 0 12px 0 4px;
`;

export const ImagePlaceholderIconWrapper = styled(ImagePlaceholderIcon, { label: 'ImagePlaceholderIconWrapper' })`
    fill: ${list.listItem.icon.bgColorSecondary};
    position: absolute;
    margin: auto;
    inset: 0;
`;

interface ListItemIconPropsType {
    isDragged?: boolean;
    status?: 'live' | 'scheduled' | 'ended' | '';
    subsection?: 'one' | 'two';
}

export const ChevronIconWrapper = styled(ChevronIcon, { label: 'ChevronIconWrapper' })<ListItemIconPropsType>`
    height: 16px;
    width: 16px;
    margin-right: 8px;
    fill: ${({ subsection }): string => subsection === 'one' ? list.listItem.icon.bgColorSecondary : subsection === 'two' ? list.listItem.icon.bgColorTertiary : list.listItem.icon.bgColor};
`;

export const DragAndDropIconWrapper = styled(DragIcon, { label: 'DragAndDropIconWrapper' })<ListItemIconPropsType>`
    height: 16px;
    width: 16px;
    cursor: ${({ isDragged }): string => isDragged === true ? 'grabbing' : 'grab'};
    fill: ${({ subsection }): string => subsection === 'one' ? list.listItem.icon.bgColorSecondary : subsection === 'two' ? list.listItem.icon.bgColorTertiary : list.listItem.icon.bgColor};
    ${({ status }): string => status === 'ended' ? 'opacity: 0.4; pointer-events: none' : ''};
`;

export const MovableSpan = styled('span', { label: 'MovableSpan' })`
    width: 16px;
    height: 16px;
    margin-right: 8px;
`;

interface ArrowWrapperPropsType {
    isDisabled?: boolean;
}

export const OrderWithArrowWrapper = styled('div', { label: 'OrderWithArrowWrapper' })<ArrowWrapperPropsType>`
    ${({ isDisabled }): string => isDisabled === true ? 'pointer-events: none' : ''};
    display: flex;
    align-items: center;
    cursor: pointer;
    color: ${({ isDisabled }): string => isDisabled === true ? lpPageColors.disabledMoveOrderColor : lpPageColors.moveOrderColor};
    &:last-of-type {
        margin-left: 8px;
    }
`;

/** @deprecated - do not use unless necessary - all movable items should be using drag&drop */
export const ArrowIconWrapperTemporary = styled(ArrowIcon, { label: 'ArrowIconWrapperTemporary' })<ArrowWrapperPropsType>`
    width: 16px;
    flex: 0 0 16px;
    fill: ${list.listItem.icon.bgColor};
    cursor: pointer;
    ${({ isDisabled }): string => isDisabled === true ? 'pointer-events: none; opacity: .4' : ''};
    margin-right: 8px;

    &:last-of-type {
        margin-top: 4px;
    }
`;

export const OrderItem = styled('div', { label: 'OrderItem' })`
    align-items: center;
    display: flex;
    flex: 1 1 100%;
    font-size: ${fontSize.xRegular};
    font-weight: ${fontWeight.bold};
    justify-content: center;
    line-height: 1.4;
    margin-left: 8px;
`;

export const ButtonsWrapper = styled('div', { label: 'ButtonsWrapper' })`
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    gap: 16px;
    align-items: center;
    margin-top: 8px;
    justify-content: space-between;
`;

export const ActionsWrapper = styled('div', { label: 'ActionsWrapper' })`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: row nowrap;
`;

export const ToggleWrapper = styled(Toggle, { label: 'ToggleWrapper' })<{status?: 'live' | 'scheduled' | 'ended' | ''}>`
    margin-bottom: 0;
    ${({ status }): string => status === 'ended' ? 'opacity: 0.4; pointer-events: none' : ''};
`;

export const CheckboxWrapper = styled('div', { label: 'CheckboxWrapper' })`
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
`;

interface ArrowIconWrapperPropsType {
    position: 'up' | 'right' | 'down' | 'left';
}

export const ArrowIconWrapper = styled(ArrowIcon, { label: 'ArrowIconWrapper' })<ArrowIconWrapperPropsType>`
    width: 16px;
    flex: 0 0 16px;
    fill: currentcolor;
`;

export const TrashIconWrapper = styled(RemoveTrashIcon, { label: 'TrashIconWrapper' })`
    cursor: pointer;
    margin-left: 8px;
`;

export const ErrorMessageTop = styled('div', { label: 'ErrorMessageTop' })`
    position: absolute;
    margin-left: 200px;
    top: 0;
    left: 0;
    right: 0;
    flex: 1 0 100%;
    font-size: 12px;
    padding: 5px 15px;
    background-color: ${mainColors.status.error};;
    color: ${mainColors.default.light};
    z-index: 1;
`;

// to be possibly deleted after global and casino pages refactor
/**
 * @deprecated - to be possibly deleted after global and casino pages refactor
 */
export const HeaderWithButtonWrapper = styled('div', { label: 'HeaderWithButtonWrapper' })`
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
`;
